<script>import { Mode } from '../../types.js';
import TextMode from './textmode/TextMode.svelte';
import TableMode from './tablemode/TableMode.svelte';
import TreeMode from './treemode/TreeMode.svelte';
import { isMenuSpaceItem } from '../../typeguards.js';
export let content;
export let readOnly;
export let indentation;
export let tabSize;
export let mode;
export let mainMenuBar;
export let navigationBar;
export let statusBar;
export let askToFormat;
export let escapeControlCharacters;
export let escapeUnicodeCharacters;
export let flattenColumns;
export let parser;
export let parseMemoizeOne;
export let validator;
export let validationParser;
export let pathParser;
export let insideModal;
export let onChange;
export let onRenderValue;
export let onClassName;
export let onRenderMenu;
export let onChangeMode;
export let onError;
export let onFocus;
export let onBlur;
export let onSortModal;
export let onTransformModal;
export let onJSONEditorModal;
let refTreeMode;
let refTableMode;
let refTextMode;
let modeMenuItems;
$: modeMenuItems = [
    {
        type: 'button',
        text: 'text',
        title: `Switch to text mode (current mode: ${mode})`,
        // check for 'code' mode is here for backward compatibility (deprecated since v0.4.0)
        className: 'jse-group-button jse-first' +
            (mode === Mode.text || mode === 'code' ? ' jse-selected' : ''),
        onClick: () => onChangeMode(Mode.text)
    },
    {
        type: 'button',
        text: 'tree',
        title: `Switch to tree mode (current mode: ${mode})`,
        className: 'jse-group-button ' + (mode === Mode.tree ? ' jse-selected' : ''),
        onClick: () => onChangeMode(Mode.tree)
    },
    {
        type: 'button',
        text: 'table',
        title: `Switch to table mode (current mode: ${mode})`,
        className: 'jse-group-button jse-last' + (mode === Mode.table ? ' jse-selected' : ''),
        onClick: () => onChangeMode(Mode.table)
    }
];
const separatorMenuItem = {
    separator: true
};
let handleRenderMenu;
$: handleRenderMenu = (items) => {
    const updatedItems = isMenuSpaceItem(items[0])
        ? modeMenuItems.concat(items) // menu is empty, readOnly mode
        : modeMenuItems.concat(separatorMenuItem, items);
    return onRenderMenu(updatedItems, { mode, modal: insideModal }) || updatedItems;
};
export function patch(operations) {
    if (refTreeMode) {
        // Note that tree mode has an optional afterPatch callback.
        // right now we don's support this in the public API.
        return refTreeMode.patch(operations);
    }
    if (refTableMode) {
        // Note that tree mode has an optional afterPatch callback.
        // right now we don's support this in the public API.
        return refTableMode.patch(operations);
    }
    if (refTextMode) {
        return refTextMode.patch(operations);
    }
}
export function expand(callback) {
    if (refTreeMode) {
        return refTreeMode.expand(callback);
    }
    else {
        throw new Error(`Method expand is not available in mode "${mode}"`);
    }
}
/**
 * Open the transform modal
 */
export function transform(options) {
    if (refTextMode) {
        refTextMode.openTransformModal(options);
    }
    else if (refTreeMode) {
        refTreeMode.openTransformModal(options);
    }
    else if (refTableMode) {
        refTableMode.openTransformModal(options);
    }
    else {
        throw new Error(`Method transform is not available in mode "${mode}"`);
    }
}
/**
 * Validate the contents of the editor using the configured validator.
 * Returns a parse error or a list with validation warnings
 */
export function validate() {
    if (refTextMode) {
        return refTextMode.validate();
    }
    else if (refTreeMode) {
        return refTreeMode.validate();
    }
    else if (refTableMode) {
        return refTableMode.validate();
    }
    else {
        throw new Error(`Method validate is not available in mode "${mode}"`);
    }
}
/**
 * In tree mode, invalid JSON is automatically repaired when loaded. When the
 * repair was successful, the repaired contents are rendered but not yet
 * applied to the document itself until the user clicks "Ok" or starts editing
 * the data. Instead of accepting the repair, the user can also click
 * "Repair manually instead". Invoking `.acceptAutoRepair()` will
 * programmatically accept the repair. This will trigger an update,
 * and the method itself also returns the updated contents. In case of text
 * mode or when the editor is not in an "accept auto repair" status, nothing
 * will happen, and the contents will be returned as is.
 */
export function acceptAutoRepair() {
    if (refTreeMode) {
        return refTreeMode.acceptAutoRepair();
    }
    else {
        return content;
    }
}
export function scrollTo(path) {
    if (refTreeMode) {
        return refTreeMode.scrollTo(path);
    }
    else if (refTableMode) {
        return refTableMode.scrollTo(path);
    }
    else {
        // TODO: implement scrollTo for text mode
        throw new Error(`Method scrollTo is not available in mode "${mode}"`);
    }
}
export function findElement(path) {
    if (refTreeMode) {
        return refTreeMode.findElement(path);
    }
    else if (refTableMode) {
        return refTableMode.findElement(path);
    }
    else {
        throw new Error(`Method findElement is not available in mode "${mode}"`);
    }
}
export function focus() {
    if (refTextMode) {
        refTextMode.focus();
    }
    else if (refTreeMode) {
        refTreeMode.focus();
    }
    else if (refTableMode) {
        refTableMode.focus();
    }
}
export async function refresh() {
    if (refTextMode) {
        await refTextMode.refresh();
    }
    else {
        // nothing to do in tree or table mode (also: don't throw an exception or so,
        // that annoying having to reckon with that when using .refresh()).
    }
}
</script>

{#if mode === Mode.text || mode === 'code'}
  <TextMode
    bind:this={refTextMode}
    externalContent={content}
    {readOnly}
    {indentation}
    {tabSize}
    {mainMenuBar}
    {statusBar}
    {askToFormat}
    {escapeUnicodeCharacters}
    {parser}
    {validator}
    {validationParser}
    {onChange}
    {onChangeMode}
    {onError}
    {onFocus}
    {onBlur}
    onRenderMenu={handleRenderMenu}
    {onSortModal}
    {onTransformModal}
  />
{:else if mode === Mode.table}
  <TableMode
    bind:this={refTableMode}
    externalContent={content}
    {readOnly}
    {mainMenuBar}
    {escapeControlCharacters}
    {escapeUnicodeCharacters}
    {flattenColumns}
    {parser}
    {parseMemoizeOne}
    {validator}
    {validationParser}
    {indentation}
    {onChange}
    {onChangeMode}
    {onRenderValue}
    {onFocus}
    {onBlur}
    onRenderMenu={handleRenderMenu}
    {onSortModal}
    {onTransformModal}
    {onJSONEditorModal}
  />
{:else}
  <!-- mode === Mode.tree -->
  <TreeMode
    bind:this={refTreeMode}
    externalContent={content}
    {readOnly}
    {indentation}
    {mainMenuBar}
    {navigationBar}
    {escapeControlCharacters}
    {escapeUnicodeCharacters}
    {parser}
    {parseMemoizeOne}
    {validator}
    {validationParser}
    {pathParser}
    {onError}
    {onChange}
    {onChangeMode}
    {onRenderValue}
    {onClassName}
    {onFocus}
    {onBlur}
    onRenderMenu={handleRenderMenu}
    {onSortModal}
    {onTransformModal}
    {onJSONEditorModal}
  />
{/if}
